import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Star, Plus, X, Award } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import PageHeader from '@/components/ui/PageHeader';
import ImageUpload from '@/components/ui/ImageUpload';
import Card from '@/components/ui/Card';
import Avatar from '@/components/ui/Avatar';
import EmptyState from '@/components/ui/EmptyState';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Stat from '@/components/ui/Stat';
import Spinner from '@/components/ui/Spinner';
import PlanSection from './PlanSection';
import { TRAINER_STATUS, fmtNum } from '@/lib/enums';
import { maskMoney, moneyToNumber, numberToMoney } from '@/lib/masks';
import styles from './Profile.module.css';

const EMPTY = {
  avatar: '',
  slug: '',
  bio: '',
  city: '',
  consultationPrice: '',
  presentationVideoUrl: '',
  specialties: [],
  certifications: [],
  socialLinks: {},
};

export default function TrainerProfile() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState({ ratingAvg: null, ratingCount: null, status: null });
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ avg: null, count: 0 });

  useEffect(() => {
    setLoading(true);
    api
      .get('/trainers/me')
      .then((res) => {
        const p = (res.data || res).profile || res.data || {};
        setForm({
          avatar: user?.avatar || '',
          slug: p.slug || '',
          bio: p.bio || '',
          city: p.city || '',
          consultationPrice: numberToMoney(p.consultationPrice),
          presentationVideoUrl: p.presentationVideoUrl || '',
          specialties: Array.isArray(p.specialties) ? p.specialties : [],
          certifications: Array.isArray(p.certifications) ? p.certifications : [],
          socialLinks: p.socialLinks && typeof p.socialLinks === 'object' ? p.socialLinks : {},
        });
        setMeta({ ratingAvg: p.ratingAvg ?? null, ratingCount: p.ratingCount ?? null, status: p.status ?? null });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Avaliações recebidas pelo personal (targetId = id do usuário logado).
  useEffect(() => {
    if (!user?.id) return;
    api
      .get('/reviews', { targetType: 'trainer', targetId: user.id })
      .then((res) => {
        const d = res.data || res;
        setReviews(Array.isArray(d.reviews) ? d.reviews : []);
        setReviewSummary({ avg: d.summary?.avg ?? null, count: d.summary?.count ?? 0 });
      })
      .catch(() => {
        setReviews([]);
        setReviewSummary({ avg: null, count: 0 });
      });
  }, [user?.id]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const setSocial = (key, value) =>
    setForm((f) => ({ ...f, socialLinks: { ...f.socialLinks, [key]: value } }));

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const payload = {
        avatar: form.avatar || null,
        ...(form.slug ? { slug: form.slug } : {}),
        bio: form.bio,
        city: form.city,
        consultationPrice: moneyToNumber(form.consultationPrice),
        presentationVideoUrl: form.presentationVideoUrl,
        specialties: form.specialties,
        certifications: form.certifications,
        socialLinks: Object.fromEntries(
          Object.entries(form.socialLinks).filter(([, v]) => v && String(v).trim())
        ),
      };
      await api.put('/trainers/me', payload);
      // Reflete a nova foto no estado global (header/menu).
      updateUser({ avatar: form.avatar || null });
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const status = meta.status ? TRAINER_STATUS[meta.status] || { label: meta.status, variant: 'neutral' } : null;

  if (loading) {
    return (
      <>
        <PageHeader kicker="Movez · Personal" title={t('nav.profile')} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.profile')}>
        <Button icon={Save} loading={saving} onClick={save}>{t('common.save')}</Button>
      </PageHeader>

      <div className={styles.metaRow}>
        <Stat
          value={meta.ratingAvg == null ? '—' : Number(meta.ratingAvg).toFixed(1)}
          label={t('profile.avgRating')}
          tone="accent"
          icon={<Star size={16} fill="var(--warning)" color="var(--warning)" />}
        />
        <Stat value={fmtNum(meta.ratingCount ?? 0)} label={t('profile.ratings')} tone="hi" />
        <div className={styles.statusCard}>
          <span className="kicker">{t('screens.status')}</span>
          {status ? <Badge variant={status.variant}>{status.label}</Badge> : <span className={styles.muted}>—</span>}
        </div>
      </div>

      <div className={styles.grid}>
        <Card padding="lg" className={styles.col}>
          <div className="kicker" style={{ marginBottom: 14 }}>{t('profile.aboutYou')}</div>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <ImageUpload
              label={t('profile.profilePhoto')}
              folder="avatars"
              round
              value={form.avatar}
              onChange={(url) => set('avatar', url)}
            />
          </div>
          <Textarea
            label={t('profile.bio')}
            rows={5}
            value={form.bio}
            onChange={(e) => set('bio', e.target.value)}
            placeholder={t('profile.bioPlaceholder')}
          />
          {/* Página pública (link da bio) */}
          <div style={{ margin: 'var(--space-4) 0' }}>
            <Input
              label={t('profile.publicPage')}
              value={form.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              placeholder="seu-nome"
            />
            {form.slug && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--mid)' }}>
                <span>{`${window.location.origin}/p/${form.slug}`}</span>
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: 'var(--volt)', cursor: 'pointer', fontSize: 12 }}
                  onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/p/${form.slug}`)}
                >
                  {t('profile.copyLink')}
                </button>
                <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" style={{ color: 'var(--frost)', fontSize: 12 }}>
                  {t('profile.viewPage')}
                </a>
              </div>
            )}
          </div>

          <div className={styles.two}>
            <Input label={t('profile.city')} value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="São Paulo" />
            <Input
              label={t('profile.consultationPrice')}
              inputMode="numeric"
              value={form.consultationPrice}
              onChange={(e) => set('consultationPrice', maskMoney(e.target.value))}
              placeholder="0,00"
            />
          </div>
          <Input
            label={t('profile.presentationVideo')}
            value={form.presentationVideoUrl}
            onChange={(e) => set('presentationVideoUrl', e.target.value)}
            placeholder="https://youtube.com/..."
          />
        </Card>

        <Card padding="lg" className={styles.col}>
          <div className="kicker" style={{ marginBottom: 14 }}>{t('profile.specialtiesCerts')}</div>
          <ChipEditor
            label={t('profile.specialties')}
            placeholder={t('profile.specialtiesPlaceholder')}
            items={form.specialties}
            onChange={(v) => set('specialties', v)}
          />
          <ChipEditor
            label={t('profile.certifications')}
            placeholder={t('profile.certificationsPlaceholder')}
            icon={Award}
            items={form.certifications}
            onChange={(v) => set('certifications', v)}
          />
          <div className={styles.social}>
            <div className="kicker" style={{ marginBottom: 6 }}>{t('profile.socialLinks')}</div>
            <Input
              label="Instagram"
              value={form.socialLinks.instagram || ''}
              onChange={(e) => setSocial('instagram', e.target.value)}
              placeholder={t('profile.instagramPlaceholder')}
            />
            <Input
              label="Website"
              value={form.socialLinks.website || ''}
              onChange={(e) => setSocial('website', e.target.value)}
              placeholder="https://..."
            />
          </div>
        </Card>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {saved && !error && <div className={styles.success}>{t('profile.savedSuccess')}</div>}

      <div style={{ marginTop: 'var(--space-6)' }}>
        <ReviewsSection reviews={reviews} summary={reviewSummary} />
      </div>

      <div style={{ marginTop: 'var(--space-6)' }}>
        <PlanSection />
      </div>
    </>
  );
}

function Stars({ rating = 0, size = 15 }) {
  return (
    <span className={styles.stars} aria-label={`${rating}/5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          strokeWidth={2}
          fill={n <= rating ? 'var(--volt)' : 'none'}
          color={n <= rating ? 'var(--volt)' : 'var(--line)'}
        />
      ))}
    </span>
  );
}

function ReviewsSection({ reviews, summary }) {
  const { t, i18n } = useTranslation();
  const fmtDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <Card padding="lg">
      <div className={styles.reviewsHead}>
        <div className="kicker">{t('reviews.title')}</div>
        {summary.count > 0 && (
          <div className={styles.reviewsSummary}>
            <Star size={16} strokeWidth={2} fill="var(--volt)" color="var(--volt)" />
            <strong>{summary.avg == null ? '—' : Number(summary.avg).toFixed(1)}</strong>
            <span className={styles.muted}>· {summary.count}</span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <EmptyState
          icon={Star}
          title={t('reviews.emptyTitle')}
          description={t('reviews.emptyDesc')}
        />
      ) : (
        <div className={styles.reviewList}>
          {reviews.map((r) => (
            <div key={r.id} className={styles.review}>
              <Avatar name={r.author?.name} src={r.author?.avatar} size={36} />
              <div className={styles.reviewBody}>
                <div className={styles.reviewTop}>
                  <span className={styles.reviewAuthor}>{r.author?.name || '—'}</span>
                  <span className={styles.muted}>{fmtDate(r.createdAt)}</span>
                </div>
                <Stars rating={Number(r.rating) || 0} />
                {r.comment && <p className={styles.reviewComment}>{r.comment}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ChipEditor({ label, placeholder, items, onChange, icon: Icon }) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const add = () => {
    const v = value.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setValue('');
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className={styles.chips}>
      <label className={styles.chipsLabel}>{label}</label>
      <div className={styles.chipRow}>
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          style={{ flex: 1 }}
        />
        <Button variant="outline" icon={Plus} onClick={add} type="button">{t('profile.add')}</Button>
      </div>
      {items.length > 0 && (
        <div className={styles.chipList}>
          {items.map((it, i) => (
            <span key={it} className={styles.chip}>
              {Icon && <Icon size={13} />}
              {it}
              <button type="button" onClick={() => remove(i)} aria-label={t('profile.remove')}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
